import React, { useState, useMemo, useCallback } from "react";
import {
  Box, Button, List, ListItemText, ListItemAvatar,
  Avatar, Menu, ListItemButton, Stack, Typography,
} from '@mui/material';
import { styled, alpha } from '@mui/material/styles';
import Tooltip, { tooltipClasses } from '@mui/material/Tooltip';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import { useRouter } from "next/router";
import Link from "next/link";

import { dataConstants } from "../../constants";
import LoginMenu from "./LoginMenu";
import useAuth from "../../hook/auth";

const NavTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.arrow}`]: { color: alpha('#0a0e1a', 0.9) },
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: alpha('#0a0e1a', 0.9),
    fontWeight: 500,
    color: '#fff',
    boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
    fontSize: 11,
    padding: '5px 10px',
    borderRadius: '6px',
    letterSpacing: '0.01em',
  },
}));

const DROPDOWN_PAPER_SX = {
  mt: 1.5,
  borderRadius: '14px',
  boxShadow: '0 4px 6px -2px rgba(0,0,0,0.06), 0 12px 36px -4px rgba(0,0,0,0.12)',
  border: '1px solid rgba(0,0,0,0.06)',
  overflow: 'hidden',
};

const SIDEBAR_LINKS = [
  { href: '/pricing', label: 'Pricing' },
  { href: '/pricing#benefits', label: 'Premium Benefits' },
  { href: '/report-issue', label: 'Report an Issue' },
];

export default function MainMenu({ pageLoading }) {
  const router = useRouter();
  const { user } = useAuth() || {};
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleClick = useCallback((event) => setAnchorEl(event.currentTarget), []);
  const handleClose = useCallback(() => setAnchorEl(null), []);

  const { subMenu, regularMenu } = useMemo(() => ({
    subMenu: dataConstants?.PAGES?.filter(item => item.subMenu) ?? [],
    regularMenu: dataConstants?.PAGES?.filter(item => !item.subMenu) ?? [],
  }), []);

  const isPageActive = useCallback((pageUrl) => {
    if (!router.isReady) return false;
    const currentPath = router.asPath.split('?')[0].split('#')[0];
    return currentPath === pageUrl || currentPath.startsWith(pageUrl + "/");
  }, [router.isReady, router.asPath]);

  return (
    <Box
      component="nav"
      aria-label="Main navigation"
      sx={{
        flexGrow: 1,
        display: { xs: "none", md: "flex" },
        justifyContent: 'flex-end',
        alignItems: 'center',
        gap: 0.25,
      }}
    >
      {regularMenu.map((page) => {
        const active = isPageActive(page.url);

        const navButton = (
          <Button
            key={`regular-menu-${page.name}`}
            component={Link}
            href={page.url}
            aria-current={active ? "page" : undefined}
            sx={{
              color: active ? "#fff" : alpha("#fff", 0.72),
              fontSize: '0.875rem',
              fontWeight: active ? 600 : 450,
              letterSpacing: '0.015em',
              whiteSpace: 'nowrap',
              textTransform: 'none',
              borderRadius: '8px',
              px: 1.75,
              py: 0.875,
              minHeight: 36,
              position: 'relative',
              bgcolor: active ? 'rgba(255,255,255,0.1)' : 'transparent',
              transition: 'background-color 0.15s ease, color 0.15s ease',
              '&:hover': {
                bgcolor: active ? 'rgba(255,255,255,0.13)' : 'rgba(255,255,255,0.07)',
                color: '#fff',
              },
              '&:focus-visible': {
                outline: '2px solid rgba(255,255,255,0.55)',
                outlineOffset: '2px',
              },
              ...(active && {
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  bottom: 0,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: '18px',
                  height: '2px',
                  bgcolor: 'rgba(255,255,255,0.45)',
                  borderRadius: '2px 2px 0 0',
                },
              }),
            }}
          >
            {page.name}
            {page.badge && (
              <Box
                component="span"
                sx={{
                  ml: 0.75,
                  px: 0.75,
                  py: 0.1,
                  fontSize: '0.6rem',
                  fontWeight: 700,
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                  bgcolor: 'rgba(255,255,255,0.16)',
                  color: '#fff',
                  borderRadius: '10px',
                  lineHeight: 1.6,
                  verticalAlign: 'middle',
                }}
              >
                {page.badge}
              </Box>
            )}
          </Button>
        );

        return page.desc && page.desc !== page.name ? (
          <NavTooltip
            key={`regular-menu-${page.name}`}
            title={page.desc}
            arrow
            placement="bottom"
            enterDelay={400}
          >
            {navButton}
          </NavTooltip>
        ) : (
          <React.Fragment key={`regular-menu-${page.name}`}>
            {navButton}
          </React.Fragment>
        );
      })}

      {subMenu.length > 0 && (
        <>
          <Button
            aria-controls={open ? 'more-menu' : undefined}
            aria-haspopup="true"
            aria-expanded={open ? 'true' : undefined}
            aria-label="More navigation options"
            onClick={handleClick}
            endIcon={
              <KeyboardArrowDownRoundedIcon
                sx={{
                  fontSize: '1rem !important',
                  ml: -0.5,
                  transform: open ? 'rotate(180deg)' : 'none',
                  transition: 'transform 0.2s ease',
                }}
              />
            }
            sx={{
              color: open ? '#fff' : alpha("#fff", 0.72),
              fontSize: '0.875rem',
              fontWeight: open ? 600 : 450,
              letterSpacing: '0.015em',
              textTransform: 'none',
              borderRadius: '8px',
              px: 1.75,
              py: 0.875,
              minHeight: 36,
              bgcolor: open ? 'rgba(255,255,255,0.1)' : 'transparent',
              transition: 'background-color 0.15s ease, color 0.15s ease',
              '&:hover': {
                bgcolor: open ? 'rgba(255,255,255,0.13)' : 'rgba(255,255,255,0.07)',
                color: '#fff',
              },
              '&:focus-visible': {
                outline: '2px solid rgba(255,255,255,0.55)',
                outlineOffset: '2px',
              },
            }}
          >
            More
          </Button>

          <Menu
            id="more-menu"
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            PaperProps={{ sx: DROPDOWN_PAPER_SX }}
            TransitionProps={{ timeout: 150 }}
          >
            <Stack direction="row">
              <List sx={{ p: 1, minWidth: 264 }} disablePadding>
                {subMenu.map((page) => {
                  const active = isPageActive(page.url);
                  return (
                    <ListItemButton
                      key={`submenu-${page.name}`}
                      component={Link}
                      href={page.url}
                      onClick={handleClose}
                      selected={active}
                      aria-current={active ? "page" : undefined}
                      sx={{
                        borderRadius: '8px',
                        mb: 0.25,
                        px: 1.5,
                        py: 1,
                        transition: 'background-color 0.12s ease',
                        '&:hover': { bgcolor: alpha('#001742', 0.05) },
                        '&.Mui-selected': {
                          bgcolor: alpha('#001742', 0.07),
                          '&:hover': { bgcolor: alpha('#001742', 0.1) },
                        },
                        '&:focus-visible': {
                          outline: '2px solid',
                          outlineColor: alpha('#001742', 0.35),
                          outlineOffset: '2px',
                        },
                      }}
                    >
                      <ListItemAvatar sx={{ minWidth: 44 }}>
                        <Avatar
                          aria-hidden="true"
                          sx={{
                            bgcolor: alpha('#001742', 0.07),
                            width: 34,
                            height: 34,
                            color: '#001742',
                          }}
                        >
                          {page.icon}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={page?.longName ?? page?.name}
                        secondary={page?.desc}
                        primaryTypographyProps={{
                          fontSize: '0.875rem',
                          fontWeight: 600,
                          color: '#0f172a',
                          lineHeight: 1.3,
                        }}
                        secondaryTypographyProps={{
                          fontSize: '0.75rem',
                          color: '#64748b',
                          mt: 0.3,
                          lineHeight: 1.4,
                        }}
                      />
                    </ListItemButton>
                  );
                })}
              </List>

              <Box
                sx={{
                  px: 2.5,
                  py: 2,
                  bgcolor: '#f8fafc',
                  borderLeft: '1px solid #e8edf2',
                  display: 'flex',
                  flexDirection: 'column',
                  minWidth: 148,
                }}
              >
                <Typography
                  component="p"
                  sx={{
                    fontSize: '0.68rem',
                    fontWeight: 700,
                    letterSpacing: '0.07em',
                    textTransform: 'uppercase',
                    color: '#94a3b8',
                    mb: 1.25,
                  }}
                >
                  Explore
                </Typography>
                {SIDEBAR_LINKS.map(({ href, label }) => (
                  <Box
                    key={href}
                    component={Link}
                    href={href}
                    onClick={handleClose}
                    sx={{
                      display: 'block',
                      textDecoration: 'none',
                      color: '#475569',
                      fontSize: '0.825rem',
                      fontWeight: 500,
                      py: 0.625,
                      transition: 'color 0.12s ease',
                      '&:hover': { color: '#001742' },
                      '&:focus-visible': {
                        outline: '2px solid',
                        outlineColor: alpha('#001742', 0.35),
                        outlineOffset: '2px',
                        borderRadius: '3px',
                      },
                    }}
                  >
                    {label}
                  </Box>
                ))}
              </Box>
            </Stack>
          </Menu>
        </>
      )}

      {!user?.email && !pageLoading && <LoginMenu />}
    </Box>
  );
}